import { fetchJSON as fetchAPI } from './client';

export type ExecutiveDashboard = {
  tenant_id: string;
  workspace_id?: string;
  brand_id?: string;
  revenue: number;
  revenue_growth: number;
  orders: number;
  order_growth: number;
  units_sold: number;
  conversion_rate: number;
  sessions?: number;
  ctr?: number;
  average_order_value: number;
  inventory_value: number;
  inventory_health: number;
  out_of_stock_risk: number;
  catalog_health: number;
  publishing_success: number;
  ai_utilization: number;
  ai_credits: number;
  marketplace_health: number;
  brand_health: number;
  ai_readiness_score: number;
  by_domain?: Record<string, number>;
  unread_notifications: number;
  data_freshness_ms: number;
  recent_activity?: ActivityEntry[];
  generated_at: string;
};

export type AIPerformanceSummary = {
  prompt_acceptance_rate: number;
  prompt_rejection_rate: number;
  avg_confidence: number;
  avg_generation_latency_ms: number;
  provider_cost: number;
  publishing_success_rate: number;
  rollback_rate: number;
  human_edit_rate: number;
  recommendation_acceptance: number;
  by_provider?: Record<string, number>;
  generated_at: string;
};

export type Experiment = {
  id: string;
  name: string;
  type: string;
  status: string;
  variant_a: string;
  variant_b: string;
  traffic_split: number;
  asin?: string;
  winner?: string;
  significance?: number;
  created_at: string;
};

export type ExperimentResult = {
  experiment_id: string;
  variant_a_value: number;
  variant_b_value: number;
  lift: number;
  significance: number;
  winner: string;
  confident: boolean;
  evaluated_at: string;
};

export type LearningSignal = {
  id: string;
  type: string;
  domain: string;
  resource_id?: string;
  prompt_version?: string;
  rating?: number;
  outcome?: string;
  captured_at: string;
};

export type IntelligenceRecommendation = {
  id: string;
  type: string;
  title: string;
  description: string;
  priority?: string;
  confidence: number;
  impact?: number;
  asin?: string;
  status: string;
  created_at: string;
};

export type AttributionChain = {
  id: string;
  release_id?: string;
  asin?: string;
  published_change: string;
  traffic_delta: number;
  conversion_delta: number;
  revenue_delta: number;
  confidence: number;
  explanation: string;
  correlation_only: boolean;
  generated_at: string;
};

export type PromptMetric = {
  id: string;
  prompt_version: string;
  model: string;
  provider: string;
  tokens: number;
  latency_ms: number;
  cost: number;
  acceptance_rate: number;
  improvement_hint?: string;
  recorded_at: string;
};

export type ActivityEntry = {
  id: string;
  category: string;
  action: string;
  message: string;
  created_at: string;
};

export type IntelligenceNotification = {
  id: string;
  title: string;
  body?: string;
  priority: string;
  read: boolean;
  created_at: string;
};

const API_BASE = import.meta.env.VITE_ANALYTICS_API ?? '/api/v1/analytics';

function fetchJSON<T>(path: string, init?: RequestInit): Promise<T> {
  return fetchAPI<T>(API_BASE, path, init);
}

export const analyticsApi = {
  executiveDashboard: (workspaceId?: string, brandId?: string) => {
    const params = new URLSearchParams();
    if (workspaceId) params.set('workspace_id', workspaceId);
    if (brandId) params.set('brand_id', brandId);
    const qs = params.toString();
    return fetchJSON<ExecutiveDashboard>(`/dashboard/executive${qs ? `?${qs}` : ''}`);
  },

  brandDashboard: (workspaceId?: string, brandId?: string) =>
    fetchJSON<ExecutiveDashboard>(
      `/dashboard/brand?workspace_id=${workspaceId ?? ''}&brand_id=${brandId ?? ''}`,
    ),

  marketplaceDashboard: (workspaceId?: string) =>
    fetchJSON<ExecutiveDashboard>(`/dashboard/marketplace?workspace_id=${workspaceId ?? ''}`),

  aiDashboard: (workspaceId?: string) =>
    fetchJSON<AIPerformanceSummary>(`/dashboard/ai?workspace_id=${workspaceId ?? ''}`),

  createExperiment: (body: {
    workspace_id: string;
    name: string;
    type: string;
    variant_a: string;
    variant_b: string;
    asin?: string;
    prompt_version?: string;
  }) => fetchJSON<Experiment>('/experiments', { method: 'POST', body: JSON.stringify(body) }),

  listExperiments: (workspaceId?: string) =>
    fetchJSON<Experiment[]>(`/experiments?workspace_id=${workspaceId ?? ''}`),

  startExperiment: (id: string) =>
    fetchJSON<Experiment>(`/experiments/${id}/start`, { method: 'POST' }),

  evaluateExperiment: (id: string) =>
    fetchJSON<ExperimentResult>(`/experiments/${id}/evaluate`, { method: 'POST' }),

  captureSignal: (body: {
    workspace_id: string;
    type: string;
    domain: string;
    resource_id?: string;
    prompt_version?: string;
    rating?: number;
    outcome?: string;
  }) => fetchJSON<LearningSignal>('/learning/signals', { method: 'POST', body: JSON.stringify(body) }),

  listSignals: (workspaceId?: string, limit = 50) =>
    fetchJSON<LearningSignal[]>(`/learning/signals?workspace_id=${workspaceId ?? ''}&limit=${limit}`),

  listRecommendations: (workspaceId?: string) =>
    fetchJSON<IntelligenceRecommendation[]>(`/recommendations?workspace_id=${workspaceId ?? ''}`),

  generateRecommendations: (workspaceId: string) =>
    fetchJSON<IntelligenceRecommendation[]>('/recommendations/generate', {
      method: 'POST',
      body: JSON.stringify({ workspace_id: workspaceId }),
    }),

  buildAttribution: (body: { release_id: string; asin: string; change: string }) =>
    fetchJSON<AttributionChain>('/attribution', { method: 'POST', body: JSON.stringify(body) }),

  listAttributions: (workspaceId?: string) =>
    fetchJSON<AttributionChain[]>(`/attribution?workspace_id=${workspaceId ?? ''}`),

  listPromptMetrics: (workspaceId?: string) =>
    fetchJSON<PromptMetric[]>(`/prompts/metrics?workspace_id=${workspaceId ?? ''}`),

  globalSearch: (body: { text: string; workspace_id?: string }) =>
    fetchJSON<{ experiments?: Experiment[]; recommendations?: IntelligenceRecommendation[]; total: number }>(
      '/search/global',
      { method: 'POST', body: JSON.stringify(body) },
    ),

  listActivity: (limit = 20) => fetchJSON<ActivityEntry[]>(`/activity?limit=${limit}`),

  listNotifications: (unread = true) =>
    fetchJSON<IntelligenceNotification[]>(`/notifications?unread=${unread}`),

  markNotificationRead: (id: string) =>
    fetchJSON<{ status: string }>(`/notifications/${id}/read`, { method: 'POST' }),

  diagnostics: () => fetchJSON<Record<string, unknown>>('/diagnostics'),

  // DEV-000042 BI Platform APIs
  biDashboard: (workspaceId?: string, type?: string) => {
    const params = new URLSearchParams();
    if (workspaceId) params.set('workspace_id', workspaceId);
    if (type) params.set('type', type);
    const qs = params.toString();
    return fetchJSON<BIDashboard>(`/dashboard${qs ? `?${qs}` : ''}`);
  },

  biKPIs: (workspaceId?: string) =>
    fetchJSON<{ kpis: KPIValue[] }>(`/kpis${workspaceId ? `?workspace_id=${workspaceId}` : ''}`),

  biForecast: (metricKey = 'revenue', horizonDays = 30, workspaceId?: string) => {
    const params = new URLSearchParams({ metric_key: metricKey, horizon_days: String(horizonDays) });
    if (workspaceId) params.set('workspace_id', workspaceId);
    return fetchJSON<ForecastResult>(`/forecast?${params}`);
  },

  biInsights: (workspaceId?: string) =>
    fetchJSON<{ insights: BIInsight[] }>(`/insights${workspaceId ? `?workspace_id=${workspaceId}` : ''}`),

  biExecutiveSummary: (workspaceId?: string, role = 'ceo') => {
    const params = new URLSearchParams({ role });
    if (workspaceId) params.set('workspace_id', workspaceId);
    return fetchJSON<ExecutiveSummary>(`/executive-summary?${params}`);
  },

  biQuery: (body: { metrics: string[]; workspace_id?: string; filters?: Record<string, unknown> }) =>
    fetchJSON<QueryResult>('/query', { method: 'POST', body: JSON.stringify(body) }),

  biSemanticSearch: (q: string) =>
    fetchJSON<SemanticSearchResult>(`/semantic-search?q=${encodeURIComponent(q)}`),
};

export type BIDashboard = {
  tenant_id: string;
  workspace_id?: string;
  dashboard_type: string;
  revenue: number;
  revenue_growth: number;
  gross_profit: number;
  net_profit: number;
  margin_pct: number;
  orders: number;
  order_growth: number;
  inventory_value: number;
  inventory_turnover: number;
  return_rate: number;
  customer_retention: number;
  ai_credit_consumption: number;
  automation_success: number;
  infrastructure_availability: number;
  kpis: KPIValue[];
  executive?: ExecutiveSummary;
  widgets?: { id: string; title: string; visualization: string }[];
  generated_at: string;
};

export type KPIValue = {
  key: string;
  name: string;
  domain: string;
  value: number;
  previous?: number;
  change?: number;
  unit?: string;
  status: string;
};

export type ExecutiveSummary = {
  headline: string;
  summary: string;
  key_wins?: string[];
  key_risks?: string[];
  actions?: string[];
  confidence: number;
};

export type ForecastResult = {
  metric_key: string;
  horizon_days: number;
  points: { date: string; value: number; lower?: number; upper?: number }[];
  confidence: number;
  model: string;
};

export type BIInsight = {
  id: string;
  type: string;
  title: string;
  description: string;
  domain: string;
  severity: string;
  confidence: number;
  impact?: number;
};

export type QueryResult = {
  columns: string[];
  rows: Record<string, unknown>[];
  total: number;
  query_ms: number;
};

export type SemanticSearchResult = {
  query: string;
  metrics: { key: string; name: string; domain: string; description?: string }[];
  kpis?: KPIValue[];
};
